import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, TextField, Button, Paper, InputAdornment, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { jwtDecode } from 'jwt-decode';
import { useChangeExpirePasswordMutation, useChangePasswordMutation } from './services/authenticationApi';
import { isLoginFunc } from '../../store/slices/login';
import { clearPermissionCache } from '../../hooks/usePermission';
import { globalVal } from '../../utils/global-values';
import CircularProgress from '@mui/material/CircularProgress';
import PasswordPolicyField, { PasswordPolicyRules } from '../../components/PasswordPolicyField';
import { getPasswordPolicyRegisterRules, PASSWORD_MAX_LENGTH } from '../../utils/passwordPolicy';
import { getTokenRoles } from '../../utils/jwt-roles';

const GradientBackground = styled(Box)(() => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    pointerEvents: 'none',
    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
    clipPath: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)',
    '@media (max-width: 900px)': {
        clipPath: 'none'
    }
}));

interface ResetPasswordFormData {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
    ip: string;
}

/** Strict Mode remount'ta history'ye birden fazla guard eklenmesini önler */
let changePassBackGuardActive = false;

const ChangePassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [changeExpirePassword, { isLoading: isExpireLoading }] = useChangeExpirePasswordMutation();
    const [changePassword, { isLoading: isChangeLoading }] = useChangePasswordMutation();
    const isLoading = isExpireLoading || isChangeLoading;
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requestInProgressRef = useRef(false);

    const { register, handleSubmit, formState: { errors }, watch } = useForm<ResetPasswordFormData>();
    const newPassword = watch('newPassword') ?? '';
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowConfirmPassword] = useState(false);

    // Zorunlu şifre değişikliği akışında geri tuşu anasayfaya düşmesin
    const tokenRoles = useMemo(() => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('verifyToken');
        if (!token) return [];
        try {
            return getTokenRoles(jwtDecode(token));
        } catch {
            return [];
        }
    }, []);

    const isForcedChangePassword = tokenRoles.includes('ChangeExpirePassword') || tokenRoles.includes('ChangePassword');
    const shouldUseExpirePasswordApi = tokenRoles.includes('ChangeExpirePassword');

    const leaveToLogin = useCallback(() => {
        changePassBackGuardActive = false;
        clearPermissionCache();
        globalVal.isLoggedOut = true;
        dispatch(isLoginFunc(false));
        localStorage.clear();
        navigate('/login', { replace: true });
    }, [dispatch, navigate]);

    const handleBack = useCallback(() => {
        if (isForcedChangePassword) {
            // Guard kaydı varsa browser back ile popstate handler oturumu temizleyip login'e alır
            if (changePassBackGuardActive) {
                window.history.back();
                return;
            }
            leaveToLogin();
            return;
        }
        navigate(-1);
    }, [isForcedChangePassword, leaveToLogin, navigate]);

    useEffect(() => {
        if (!isForcedChangePassword) return;

        // Geri tuşunda bir önceki (muhtemelen anasayfa) kayda düşmeden login'e git
        if (!changePassBackGuardActive) {
            window.history.pushState({ changePassGuard: true }, '');
            changePassBackGuardActive = true;
        }

        const onPopState = () => {
            leaveToLogin();
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, [isForcedChangePassword, leaveToLogin]);

    const handleChangePassword = async (data: ResetPasswordFormData) => {
        if (requestInProgressRef.current || isLoading || isSubmitting) return;
        
        requestInProgressRef.current = true;
        setIsSubmitting(true);
        
        try {
            const payload = {
                ip: "1.1.1.1",
                oldPassword: data.oldPassword,
                newPassword: data.newPassword,
                confirmPassword: data.confirmNewPassword,
            };
            const mutation = shouldUseExpirePasswordApi ? changeExpirePassword : changePassword;
            const response = await mutation(payload).unwrap();

            if (response.success) {
                enqueueSnackbar(response.message, { variant: 'success' });
                setIsFadingOut(true);
                setTimeout(() => {
                    leaveToLogin();
                }, 400); // 400ms fade süresi
                // Başarılı durumda state'leri sıfırlama - sayfa reload olacak
                return;
            }
        } catch (error: any) {
            const status = error?.status ?? error?.originalStatus;
            if (status === 401) {
                leaveToLogin();
                return;
            }
            setIsSubmitting(false);
            requestInProgressRef.current = false;
        }
    };

    return (
        <Box className={isFadingOut ? 'fade-out' : ''} sx={{
            position: 'relative',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // Taşınca ortaya hizalama üst içeriği kesmesin; zoom'da scroll üstten başlasın
            justifyContent: 'safe center',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: 0,
            width: '100%',
            pt: { xs: '72px', sm: '80px', md: '64px' },
            px: { xs: 1.5, sm: 2, md: 3 },
            pb: { xs: 3, sm: 3, md: 4 },
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
        }}>
            <IconButton
                onClick={handleBack}
                sx={{
                    position: 'fixed',
                    top: { xs: 80, sm: 88, md: 80 },
                    left: { xs: 12, md: 16 },
                    zIndex: 100,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
            >
                <ArrowBack />
            </IconButton>
            <Paper
                component="form"
                onSubmit={handleSubmit(handleChangePassword)}
                elevation={3}
                sx={{
                    width: { xs: '100%', sm: '85%', md: '90%', lg: '80%', xl: '51.4%' },
                    maxWidth: '984px',
                    flexShrink: 0,
                    padding: { xs: '0 0 16px 0', sm: 2, md: 3, lg: 4 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(255, 255, 255, 0.75)',
                    borderRadius: { xs: '25px', md: '35px' },
                    border: '1.2px solid rgba(0, 0, 0, 0.1)',
                    zIndex: 50,
                    boxSizing: 'border-box',
                    overflow: 'visible',
                    my: { md: 2 },
                }}
            >
                <Box sx={{
                    width: { xs: '32%', sm: '35%', md: '38%' },
                    maxWidth: { xs: 120, md: 220 },
                    p: { xs: 1, sm: 1.5, md: 2 },
                    pt: { xs: 2, sm: 1.5, md: 2 },
                    color: 'white',
                    flexShrink: 0,
                }}>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
                        alt="Logo"
                        sx={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
                    />
                </Box>

                <Box sx={{
                    width: { xs: '100%', md: '62%' },
                    bgcolor: { xs: 'white', md: 'transparent' },
                    p: { xs: '12px 14px 16px', md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 0.75, md: 1.5 },
                    borderRadius: { xs: '25px', md: 0 },
                    minWidth: 0,
                }}>
                    <Box sx={{ width: { xs: '100%', sm: '90%', md: '88%' }, mb: { xs: 0, md: 0.5 } }}>
                        <Typography sx={{
                            fontWeight: 400,
                            fontSize: { xs: '15px', md: '16px' },
                            lineHeight: '24px',
                            letterSpacing: '-0.4px',
                            verticalAlign: 'middle',
                            color: '#00236F',
                            mb: 0.5,
                        }}>
                            Şifrenizi Güncelleyin
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#64748b',
                            lineHeight: 1.4,
                        }}>
                            Lütfen yeni ve güçlü bir şifre belirleyin.
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: { xs: '100%', sm: '90%', md: '88%' },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 1, md: 1.5 },
                        minWidth: 0,
                    }}>
                        <TextField
                            fullWidth
                            label="Mevcut Şifre"
                            required
                            type={showPassword ? 'text' : 'password'}
                            {...register('oldPassword', {
                                required: 'Mevcut şifre gerekli',
                            })}
                            error={!!errors.oldPassword}
                            helperText={errors.oldPassword?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <PasswordPolicyField
                            fullWidth
                            label="Yeni Şifre"
                            required
                            showStrengthIndicator
                            policyValue={newPassword}
                            {...register('newPassword', getPasswordPolicyRegisterRules('Şifre alanı boş bırakılamaz'))}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                            inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
                        />

                        <TextField
                            fullWidth
                            label="Yeni Şifre Tekrar"
                            required
                            type={showNewPassword ? 'text' : 'password'}
                            {...register('confirmNewPassword', {
                                required: 'Yeni şifre tekrarı gerekli',
                                validate: (val: string) => {
                                    if (watch('newPassword') !== val) {
                                        return "Girilen şifreler eşleşmemektedir.";
                                    }
                                }
                            })}
                            error={!!errors.confirmNewPassword}
                            helperText={errors.confirmNewPassword?.message}
                            inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <PasswordPolicyRules
                            password={newPassword}
                            compact
                            sx={{ mt: { xs: 1, md: 2 }, mb: { xs: 1, md: 2 } }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            disabled={isLoading || isSubmitting}
                            sx={{
                                height: { xs: '48px', md: '52px' },
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                borderColor: '#1e3c72',
                                borderRadius: '12px',
                                color: 'white',
                                fontSize: '15px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
                                },
                                '&:disabled': {
                                    background: 'rgba(30, 60, 114, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.6)'
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {(isLoading || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : 'Şifreyi Güncelle'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <GradientBackground />

            <style>{`
                .fade-out {
                    animation: fadeOutChangePassword 0.4s forwards;
                }
                @keyframes fadeOutChangePassword {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `}</style>
        </Box>
    );
};

export default ChangePassword;
